/**
 * Small helpers for handling UK-format dates (DD/MM/YYYY) in forms.
 *
 * Used by:
 *   - onboarding PersonalDetailsStep (DOB collection)
 *   - settings YourDetailsSection (DOB display / edit)
 *
 * Storage format is always ISO YYYY-MM-DD; UK format is UI-only.
 */

/**
 * Auto-format a raw user input as UK DD/MM/YYYY.
 * Strips non-digits, inserts slashes after day and month, caps at 8 digits.
 *
 * Example: "14031985" -> "14/03/1985", "1" -> "1", "140" -> "14/0".
 */
export function formatUkDateInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Parse a UK DD/MM/YYYY string into an ISO YYYY-MM-DD string.
 * Returns null if the string is malformed or the date is not a real
 * calendar date (e.g. 31/02/2000).
 */
export function parseUkDateToIso(uk: string): string | null {
  const match = uk.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  // Reject rolled-over dates (e.g. 31 Feb -> 3 Mar).
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${match[3]}-${match[2]}-${match[1]}`;
}

/**
 * Convert an ISO YYYY-MM-DD string to UK DD/MM/YYYY for display.
 * Returns empty string if the input is falsy or malformed.
 */
export function isoDateToUk(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/**
 * Full DOB validation. Returns a user-facing error message if invalid, or
 * null if valid. Applies these rules:
 *   - required (non-empty)
 *   - valid UK DD/MM/YYYY format that parses to a real calendar date
 *   - not in the future
 *   - not before 1 Jan 1900
 *   - user must have already had their 16th birthday (soft minimum age)
 */
export function validateUkDateOfBirth(uk: string): string | null {
  const trimmed = uk.trim();
  if (!trimmed) return 'Your date of birth is required';

  const iso = parseUkDateToIso(trimmed);
  if (!iso) return 'Please enter a valid date as DD/MM/YYYY';

  const dob = new Date(`${iso}T00:00:00Z`);
  const now = new Date();
  const earliest = new Date('1900-01-01T00:00:00Z');

  if (dob > now) return 'Date of birth cannot be in the future';
  if (dob < earliest) return 'Please enter a valid date of birth';

  const sixteenthBirthday = new Date(
    Date.UTC(dob.getUTCFullYear() + 16, dob.getUTCMonth(), dob.getUTCDate()),
  );
  if (sixteenthBirthday > now) return 'You must be at least 16 to sign up';

  return null;
}
